import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { Client, type IFrame } from '@stomp/stompjs';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { defer } from 'rxjs';

import * as api from '~/api/metadata';
import Logo from '~/images/nv-logo-sv.svg?react';
import { navDrawerWidth } from '~/resources';
import * as AuthService from '~/services/authentication';
import type { TpsAdminMetaData } from '~/types/api-types';
import type { User } from '~/types/types';
import { useSubscription } from '~/util/use-subscription';

interface Activity {
  importing: { active: boolean; user: string };
  freezing: { active: boolean; user: string };
  export: { active: boolean; user: string };
  importQueueSize: number;
}

const ActivityOngoing = ({
  importing,
  freezing,
  export: exportActivity, // renamed to avoid naming conflicts with JS keyword
  importQueueSize,
}: Activity) => {
  const metadata = useSubscription(
    defer(() => api.getSystemMetaData()),
    {} as TpsAdminMetaData
  );

  const version = useSubscription(
    defer(() => getVersion()),
    ''
  );

  // Determine the activity message
  let message = 'Ingen aktivitet'; // Default message
  if (importing?.active || freezing?.active || exportActivity?.active) {
    message = [
      exportActivity?.active ? `${exportActivity.user} Exporterar` : '',
      importing?.active ? `${importing.user} Importerar` : '',
      freezing?.active ? `${freezing.user} Fryser revision` : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  const importQueueMessage = importing?.active || importQueueSize > 0 ? `Antal importer i kö: ${importQueueSize}` : '';

  return (
    <Typography variant="caption">
      Version: {version}
      <br />
      Databas: {metadata.currentDatabase}
      <br />
      {message}
      <br />
      {importQueueMessage}
    </Typography>
  );
};

const getActivity = async () => {
  try {
    const res = await axios.get('/api/getActivity');
    localStorage.setItem('activity', JSON.stringify(res.data));
  } catch (error) {
    console.error('Failed to fetch activity', error);
  }
};

const getVersion = async () => {
  try {
    const res = await axios.get('/api/getVersion');
    return res.data;
  } catch (error) {
    console.error('Failed to fetch version', error);
    return 'unknown';
  }
};

const protocol = location.hostname === 'localhost' ? 'ws' : 'wss';
const client = new Client({
  brokerURL: `${protocol}://${location.host}/api/webSocket`,
  connectHeaders: {},
  reconnectDelay: 5000,
  heartbeatIncoming: 4000,
  heartbeatOutgoing: 4000,
});

client.activate();
const noActivity = {
  importing: { active: false, user: '' },
  freezing: { active: false, user: '' },
  export: { active: false, user: '' },
};

const TopBar = (props: any) => {
  const [user, setUser] = useState<User | null>(null);
  const [activity, setActivity] = useState<Activity>(
    localStorage.getItem('activity') === null ? noActivity : JSON.parse(localStorage.getItem('activity') as string)
  );

  if (activity === noActivity) {
    getActivity().then(() => {
      setActivity(JSON.parse(localStorage.getItem('activity') as string));
    });
  }

  useEffect(() => {
    const userSub = AuthService.getUserObs().subscribe((userObs) => {
      setUser(userObs || null);
    });

    return () => {
      userSub.unsubscribe();
    };
  }, []);

  const setActivityCallback = useCallback((message: any) => {
    if (message.body) {
      setActivity(JSON.parse(message.body));
      localStorage.setItem('activity', message.body);
    } else {
      console.log('got empty message');
    }
  }, []);

  client.onConnect = () => {
    // Do something, all subscribes must be done is this callback
    // This is needed because this will be executed after a (re)connect
    client.subscribe('/topic/sendToFrontend', setActivityCallback);
  };

  client.onStompError = (frame: IFrame) => {
    // Will be invoked in case of error encountered at Broker
    // Bad login/passcode typically will cause an error
    // Complaint brokers will set `message` header with a brief message. Body may contain details.
    // Compliant brokers will terminate the connection after any error
    console.log(`Broker reported error: ${frame.headers.message}`);
    console.log(`Additional details: ${frame.body}`);
  };

  const BuildUserControls = () => {
    if (!user) {
      return null;
    }

    return (
      <div style={{ textAlign: 'center', marginLeft: '20px' }}>
        <Typography color="inherit">{user.name}</Typography>
        <Button
          sx={{
            padding: '5px',
            minHeight: 0,
            backgroundColor: 'white',
            color: 'black',
          }}
          variant="contained"
          onClick={handleLogOut}
        >
          Logga ut
        </Button>
      </div>
    );
  };

  const handleLogOut = () => {
    AuthService.logout();
  };
  return (
    <AppBar position="static">
      <Toolbar
        sx={{
          height: '5.4rem',
          marginBottom: '5px',
          ...(props.drawerOpen && {
            width: `calc(100% - ${navDrawerWidth}px)`,
            marginLeft: `${navDrawerWidth}px`,
          }),
        }}
      >
        {!props.drawerOpen && user && (
          <IconButton
            sx={{ marginRight: '24px' }}
            color="inherit"
            aria-label="Open drawer"
            onClick={props.onMenuClick}
            size="large"
          >
            <MenuIcon />
          </IconButton>
        )}
        <Link to="/" style={{ height: '100%' }}>
          <Logo style={{ height: '100%', width: 'auto', marginRight: '20px' }} />
        </Link>
        <Typography variant="h5" color="inherit" flexGrow={1}>
          {props.title}
        </Typography>
        <ActivityOngoing
          export={activity.export}
          freezing={activity.freezing}
          importQueueSize={activity.importQueueSize}
          importing={activity.importing}
        />
        {<BuildUserControls />}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
